import { useClickhouseMediaAnalytics } from '@/utils/hooks/featureFlag/useClickhouseMediaAnalytics';
import { useTransformStringOnOffset } from '@/utils/hooks/format/useTransformString';

import { classNames } from '@/utils';

import type { XMediaPartialType, XTagType } from '@/components/pages/content/types';

import { DEFAULT_POST_CONFIG } from '@/lib/posts/defaultPostConfig';
import { TW_POST_ICONS } from '@/lib/posts/postIcon';
import { getPostType, POST, TW_POST_TYPES, type TWPostTypesType } from '@/lib/posts/postTypes';
import { X } from '@/lib/socialProfile';

import Post from '@/components/base/Post';
import HoverLabelAndAlbums from '@/components/base/Post/HoverLabelAndAlbums';
import { InitialAuthorSection } from '@/components/base/Post/TwitterPost/InitialAuthorSection';
import { PollOptions } from '@/components/base/Post/TwitterPost/PollOptions';
import { TwitterPostFooter } from '@/components/base/Post/TwitterPost/TwitterPostFooter';

type PostConfig = {
    hasHover?: boolean;
    hasFooter?: boolean;
    showLabels?: boolean;
    showAlbums?: boolean;
};

type TwitterPostProps = {
    data?: XMediaPartialType;
    isLoading?: boolean;
    config?: Record<string, unknown>;
    openViewer?: () => void;
    isExport?: boolean;
    remoteMedia?: boolean;
    handleLabelsEvent?: () => void;
    handleAlbumsEvent?: () => void;
    postConfig?: PostConfig;
    showHeader?: boolean;
};

const buildTagComponents = (tags?: XTagType[]) => ({
    a: <span className="text-icoBlue" />,
    br: <br />,
    ...Object.fromEntries(
        (tags ?? []).map((tag, id) => [
            id,
            // eslint-disable-next-line jsx-a11y/anchor-has-content
            <a
                key={`${tag.link}-${id}`}
                href={tag.link}
                className="inline-block text-icoBlue"
                target="_blank"
                rel="noopener noreferrer"
            />
        ])
    )
});

const getPostTypeIconType = (
    type: TWPostTypesType | string
): (typeof TW_POST_ICONS)[keyof typeof TW_POST_ICONS] => {
    if (type === TW_POST_TYPES.TYPE_PHOTO) return 'content';
    if (type === TW_POST_TYPES.TYPE_QUOTE) return 'quotes';
    return type as (typeof TW_POST_ICONS)[keyof typeof TW_POST_ICONS];
};

const TwitterPost = ({
    data,
    isLoading,
    config = undefined,
    openViewer = undefined,
    isExport = false,
    remoteMedia = false,
    handleLabelsEvent = undefined,
    handleAlbumsEvent = undefined,
    postConfig = { hasHover: true, hasFooter: true, showLabels: true, showAlbums: true },
    showHeader = true
}: TwitterPostProps) => {
    const { OffsetReplace, formatTextWithComponent } = useTransformStringOnOffset();
    const type = getPostType({ network: X, type: data?.type });
    const mergedPostConfig = { ...DEFAULT_POST_CONFIG, ...postConfig };
    const { isActiveForNetwork } = useClickhouseMediaAnalytics();
    const shouldUseLazyAnalytics = isActiveForNetwork(X);

    if (isLoading) {
        return <Post.Placeholder postType={POST} isExport={isExport} />;
    }

    const caption = formatTextWithComponent(data?.caption, data?.tags);
    const relatedTweet = data?.data?.related_tweet;
    const _isReply = relatedTweet !== null && relatedTweet !== undefined;

    const isMediaOnlyPost =
        (type === TW_POST_TYPES.TYPE_PHOTO || type === TW_POST_TYPES.TYPE_VIDEO) &&
        !caption &&
        (data?.medias?.length ?? 0) > 0;

    const showPostTypeIcon = type && type !== TW_POST_TYPES.TYPE_STATUS;

    const postTypeIconType = getPostTypeIconType(type);

    const withMedia =
        type !== TW_POST_TYPES.TYPE_STATUS &&
        type !== TW_POST_TYPES.TYPE_LINK &&
        type !== TW_POST_TYPES.TYPE_POLL;

    const pollOptions = data?.medias?.[0]?.poll?.options;
    const showPoll = type === TW_POST_TYPES.TYPE_POLL && pollOptions;

    return (
        <div className="w-full">
            {/* @ts-ignore */}
            {showHeader && <Post.Header data={data} isExport={isExport} />}
            <Post
                data={data}
                // @ts-ignore
                config={config}
                isLoading={isLoading}
                isExport={isExport}
                className={classNames('w-full bg-white', {
                    'p-0.5': !mergedPostConfig?.hasFooter,
                    'px-2 pt-2': mergedPostConfig?.hasFooter,
                    'dark:bg-icoDarkMode-anthracite': !isExport
                })}>
                {({ referenceElement }) => (
                    <>
                        <Post.Content className="w-full">
                            {mergedPostConfig?.hasHover && (
                                <HoverLabelAndAlbums
                                    openViewer={openViewer}
                                    referenceElement={referenceElement}
                                    handleLabelsEvent={handleLabelsEvent}
                                    handleAlbumsEvent={handleAlbumsEvent}
                                    showLabels={mergedPostConfig?.showLabels}
                                    showAlbums={false} // Albums/Campaigns not available for X platform
                                    socialProfile={data?.social_profile}
                                />
                            )}
                            {showPostTypeIcon && (
                                // @ts-ignore
                                <Post.PostTypeIcon type={postTypeIconType} />
                            )}
                            {data?.promoted_post && <Post.Promoted />}

                            {isMediaOnlyPost ? (
                                <div className="square relative w-full overflow-hidden rounded-md">
                                    <Post.Image remoteMedia={remoteMedia} />
                                </div>
                            ) : (
                                <Post.Status
                                    remoteMedia={remoteMedia}
                                    withMedia={withMedia}
                                    formattedCaption={
                                        <>
                                            {data && (
                                                <InitialAuthorSection
                                                    data={data}
                                                    isExport={isExport}
                                                />
                                            )}
                                            <span className="break-words">
                                                <OffsetReplace
                                                    text={caption}
                                                    components={buildTagComponents(data?.tags)}
                                                />
                                            </span>
                                            {showPoll && <PollOptions options={pollOptions} />}
                                        </>
                                    }
                                />
                            )}
                        </Post.Content>
                        <TwitterPostFooter
                            hasFooter={mergedPostConfig?.hasFooter}
                            shouldUseLazyAnalytics={shouldUseLazyAnalytics}
                            data={data}
                            type={type}
                        />
                    </>
                )}
            </Post>
        </div>
    );
};

export default TwitterPost;
